import getSessionContext from 'src/utils/context/getSessionContext'
import { make } from 'vuex-pathify'
import makeTableCommonActions from '../../utils/vuexModuleConstructor/vuexTableCommonActionsMaker'
import makeTableCommonGetters from '../../utils/vuexModuleConstructor/vuexTableCommonGettersMaker'
import makeTableCommonState from '../../utils/vuexModuleConstructor/vuexTableCommonStateMaker'

const remoteEntityName = 'ci.acceptable_overbooking_levels'

const state = {
  ...makeTableCommonState()
}

const getters = {
  ...makeTableCommonGetters({ remoteEntityName }),

  pageTitle: () => 'Accetable overbooking levels',

  controlledActions: () => {
    return { // 0 if anyone can. -1 if disabled (i.e., no one can); 'creator' -- if only user with id same as created_by_user_id
      create: 0,
      fetch: 0,
      update: 0,
      delete: 0,
      downloadList: 0,
      uploadList: -1
    }
  },

  controllerDefaultParams: (state, getters, rootState) => {
    return {
      fetch: { org_id: getSessionContext().org_id }
    }
  },

  columns: (state, getters, rootState, rootGetters) => {
    const isDirectOverrideAlertTooltipContentt = props => {
      const currentCalculationBasedOnRules = state.list.find(r => r.class_id === props.row.class_id && !r.is_direct_override && r.is_active) || {}
      const text = `
        <span style="font-weight:500">This is a direct override:</span>
        <br>By ${props.row.created_by_user_name}
        <br>At ${Intl.DateTimeFormat([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }).format(new Date(props.row.created_at))}
        ${props.row.notes ? '<br>Notes: ' + props.row.notes : ''}
        <hr>
        <span style="font-weight:500">Values calculated based on rules:</span>
        <br>Acceptable risk of denied admission: ${(currentCalculationBasedOnRules || {}).acceptable_denied_admission_risk}
        <br>Acceptable overbooking level, spots: ${(currentCalculationBasedOnRules || {}).acceptable_overbooking_level}
        <hr>
        <span style="font-weight:500">Values in effect, at time of override:</span>
        <br>Acceptable risk of denied admission: ${(props.row.effective_values_at_time_of_override || {}).acceptable_denied_admission_risk}
        <br>Acceptable overbooking level, spots: ${(props.row.effective_values_at_time_of_override || {}).acceptable_overbooking_level}
        <hr>
        <span style="font-weight:500">Values calculated based on rules, at time of override:</span>
        <br>Acceptable risk of denied admission: ${(props.row.calculated_based_on_rules_values_at_time_of_override || {}).acceptable_denied_admission_risk}
        <br>Acceptable overbooking level, spots: ${(props.row.calculated_based_on_rules_values_at_time_of_override || {}).acceptable_overbooking_level}
        `
      return text
    }

    const rowMenuItems = [
      { name: 'deactivate_direct_override', label: 'Deactivate override', icon: 'mdi-format-strikethrough', menuItemIsShown: props => props.row.is_direct_override, callback: (props, ctx) => ctx.handleCellChange(false, props, { field: 'is_active' }) },
      { name: 'create_direct_override', label: props => `Create ${props.row.is_direct_override ? '(another) ' : ''}override`, icon: 'mdi-format-strikethrough', callback: (props, ctx) => ctx.$store.dispatch('mode/setCurrentMode', { name: 'generic-create-new-record', localEntityName: 'ci.acceptable_overbooking_levels', onlyShowTheseColumns: [ 'acceptable_denied_admission_risk', 'acceptable_overbooking_level', 'notes' ], columnToAutofocusWhenCreating: 'acceptable_denied_admission_risk', createNewRecordTitle: 'Enter override inputs', createNewRecordSubtitle: 'Please enter only one of the values. The other will be calculated automatically', filters: { class_id: props.row.class_id, is_direct_override: true } }) }
    ]

    return [
      { name: 'row_menu', type: 'row_menu', menuItems: rowMenuItems, hideWhenCreating: true },
      { name: 'id', label: 'ID', style: 'width:50px', field: 'id', align: 'left', hideWhenCreating: true, isNumeric: true },
      { name: 'is_direct_override_alert', isIcon: true, icon: 'mdi-alert', tooltipContentIsHTML: true, tooltipContent: props => isDirectOverrideAlertTooltipContentt(props), isHidden: data => !data.some(r => r.is_direct_override), cellContentIsShown: props => props.row.is_direct_override, tdStyles: { padding: '4px 0 4px 4px', color: 'var(--q-color-negative)' } },
      { name: 'is_direct_override_info', isIcon: true, icon: 'mdi-information-variant', tooltipContentIsHTML: true, tooltipContent: props => isDirectOverrideAlertTooltipContentt(props), isHidden: data => !data.some(r => r.is_direct_override), cellContentIsShown: props => props.row.is_direct_override, tdStyles: { padding: '4px 0 4px 4px', color: 'var(--q-color-dark)' } },
      { name: 'notes', style: 'min-width: 150px', label: 'Notes', field: 'notes' },
      { name: 'notes_icon', isIcon: true, icon: 'mdi-text', tooltipContent: props => props.row.notes, cellContentIsShown: props => props.row.notes && props.row.notes.length > 0 },

      ...rootGetters['ci.availability/columns'].filter(col => ['class_id', 'class_location', 'class_type', 'class_date', 'class_time', 'is_active', 'is_currently_in_effect'].includes(col.name)),

      { name: 'acceptable_denied_admission_risk', tooltipContent: 'Acceptable denied admission risk', label: 'Acceptable denied admission risk', field: 'acceptable_denied_admission_risk', isPercent: true, defaultValueWhenCreating: 0 },
      { name: 'acceptable_overbooking_level', tooltipContent: 'Acceptable overbooking level, spot count', label: 'Acceptable overbooking level, spot count', field: 'acceptable_overbooking_level', isNumeric: true, isInteger: true, defaultValueWhenCreating: 0 },

      ...rootGetters['ci.availability/columns'].filter(col => ['is_direct_override', 'created_at', 'created_by_user_id', 'created_by_user_name', 'inactivated_at', 'inactivated_by_user_name', 'became_effective_at', 'stopped_being_effective_at'].includes(col.name))
    ]
  }
}

const mutations = make.mutations(state)
const namespaced = true

const actions = {
  ...makeTableCommonActions()
}

export default { state, actions, mutations, getters, namespaced }
